// SOFRA · 10 paket · BÜYÜK KARTLAR · asimetrik grid

import { useRef, useState } from "react";
import { SectionShell } from "@/features/landing/components/SectionShell";
import { Lightbox, type LightboxImage } from "@/components/ui/Lightbox";
import { COPY } from "@/features/landing/content/landing-copy";
import { useSofraMotion } from "./Sofra.motion";
import styles from "./Sofra.module.css";

const LIGHTBOX_IMAGES: readonly LightboxImage[] = COPY.sofra.packages.map((pkg) => ({
  src: `/${pkg.image}`,
  alt: `${pkg.name} hizmet — ${pkg.level}`,
  caption: `${pkg.name} · ${pkg.level}`,
  subcaption: pkg.spec.join(" · "),
}));

export function Sofra() {
  const scopeRef = useRef<HTMLElement | null>(null);
  useSofraMotion(scopeRef);

  const [zoom, setZoom] = useState<number | null>(null);

  return (
    <SectionShell id="sofra" ariaLabelledBy="sofra-title" tone="paper" container="wide">
      <div className={styles.sofra} ref={scopeRef as unknown as React.RefObject<HTMLDivElement>}>
        <header className={styles.sofra__head}>
          <div>
            <p className={styles.sofra__eyebrow}>{COPY.sofra.chapter} · {COPY.sofra.eyebrow}</p>
            <h2 className={styles.sofra__title} id="sofra-title">{COPY.sofra.title}</h2>
          </div>
          <p className={styles.sofra__lede}>{COPY.sofra.lede}</p>
        </header>

        <ol className={styles.sofra__grid} role="list">
          {COPY.sofra.packages.map((pkg, idx) => (
            <PackageCard key={pkg.id} pkg={pkg} index={idx + 1} onZoom={() => setZoom(idx)} />
          ))}
        </ol>
      </div>

      <Lightbox
        images={LIGHTBOX_IMAGES}
        index={zoom}
        onClose={() => setZoom(null)}
        onNavigate={setZoom}
        label="Sofra hizmet görselleri"
      />
    </SectionShell>
  );
}

interface PackageCardProps {
  pkg: typeof COPY.sofra.packages[number];
  index: number;
  onZoom: () => void;
}

function PackageCard({ pkg, index, onZoom }: PackageCardProps) {
  const waText = encodeURIComponent(
    `Merhaba, ${pkg.name} hizmet tarife (${pkg.level}) hakkında bilgi almak istiyorum.`
  );
  const waHref = `${COPY.sofra.whatsappBase}?text=${waText}`;

  return (
    <li className={`${styles.card} ${pkg.accent ? styles["card--platin"] : ""}`.trim()}>
      <figure className={styles.card__image}>
        <button
          type="button"
          className={styles.card__zoom}
          onClick={onZoom}
          aria-label={`${pkg.name} görselini büyüt`}
        >
          <img
            src={`/${pkg.image}`}
            alt={`${pkg.name} hizmet — ${pkg.level}`}
            width={1448}
            height={1086}
            loading="eager"
            decoding="async"
            fetchPriority={index <= 3 ? "high" : "auto"}
          />
          <span className={styles["card__zoom-hint"]} aria-hidden="true">Büyüt +</span>
        </button>
        <span className={styles.card__index}>{String(index).padStart(2, "0")}</span>
      </figure>

      <div className={styles.card__body}>
        <span className={styles.card__level}>{pkg.name}</span>
        <h3 className={styles.card__name}>{pkg.level}</h3>
        <div className={styles.card__specs}>
          {pkg.spec.map((s, i) => (
            <span key={i}>— {s}</span>
          ))}
        </div>
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.card__cta}
          aria-label={`${pkg.name} hizmeti için WhatsApp'tan yaz`}
        >
          <svg
            className={styles["card__cta-icon"]}
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              fill="currentColor"
              d="M17.5 14.4c-.3-.2-1.8-.9-2.1-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-1 1.2-.2.2-.4.2-.7.1-.3-.2-1.3-.5-2.5-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.7.1-.1.3-.4.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.3 5.2 4.6.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.4 5L2 22l5.2-1.4c1.4.8 3 1.2 4.7 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.3c-1.6 0-3.1-.4-4.4-1.2l-.3-.2-3.2.8.9-3.1-.2-.3c-.9-1.4-1.3-2.9-1.3-4.5 0-4.6 3.7-8.3 8.3-8.3 4.6 0 8.3 3.7 8.3 8.3.1 4.6-3.6 8.5-8.1 8.5z"
            />
          </svg>
          WhatsApp'tan yaz
        </a>
      </div>
    </li>
  );
}
